import { useEffect, useMemo, useRef } from "react";
import { mergeStreamingWithFirestore } from "./messageMerger";
import type { Message } from '@generated/proto/model'

/**
 * Efficiently compares two string arrays for equality (order-independent)
 */
function arraysEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false
  
  // For small arrays, simple sort + comparison is efficient
  if (a.length <= 10) {
    const sortedA = [...a].sort()
    const sortedB = [...b].sort()
    return sortedA.every((val, idx) => val === sortedB[idx])
  }
  
  // For larger arrays, use Set-based comparison
  const setA = new Set(a)
  const setB = new Set(b)
  return setA.size === setB.size && [...setA].every(val => setB.has(val))
}

interface MessageRow {
  id: string
  proto: Message
  created?: Date
  modified?: Date
}

interface StreamingMessage {
  messageId: string
  content: string
  isComplete: boolean
  created: Date
}

interface MessageSyncReturn {
  /**
   * The final merged messages to display
   */
  messages: MessageRow[]
}

/**
 * Hook that manages the synchronization between Firestore messages and streaming state.
 * This hook handles the timing of when to transition from streaming to final state
 * without causing visual jumps.
 */
export function useMessageSync(
  firestoreMessages: MessageRow[],
  streamingMessages: Map<string, StreamingMessage>,
  clearMultiple: (messageIds: string[]) => void
): MessageSyncReturn {
  // Store the last cleanup to avoid duplicate cleanups
  const lastCleanupRef = useRef<string[]>([])

  // Compute merged messages and what needs cleanup
  const mergeResult = useMemo(() => {
    const result = mergeStreamingWithFirestore(firestoreMessages, streamingMessages)
    
    // Check if cleanup list changed to avoid unnecessary effects
    const cleanupChanged = !arraysEqual(result.toCleanup, lastCleanupRef.current)
    if (cleanupChanged) {
      lastCleanupRef.current = [...result.toCleanup]
    }
    
    return result
  }, [firestoreMessages, streamingMessages])

  // Handle cleanup with a small delay to ensure Firestore stability
  useEffect(() => {
    if (mergeResult.toCleanup.length === 0) return
    
    // Small delay to ensure Firestore message is fully rendered before cleanup
    const timeoutId = setTimeout(() => {
      clearMultiple(mergeResult.toCleanup)
    }, 100) // 100ms delay
    
    return () => clearTimeout(timeoutId)
  }, [mergeResult.toCleanup, clearMultiple])

  return {
    messages: mergeResult.messages
  }
}